import * as Sentry from '@sentry/react';
import { ComponentPropsWithoutRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { RouteComponentProps } from 'react-router';
import { Route } from 'react-router-dom';
import { CompatRoute, Routes } from 'react-router-dom-v5-compat';

// This higher order component helps us get parameterized event names in Sentry
// https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/#parameterized-transaction-names
export const SentryRouteBase = Sentry.withSentryRouting(Route);

// For migration to react-router v6
export const SentryRouteBaseV6 = Sentry.withSentryRouting(CompatRoute);

// For exclusive use with react-router v6
export const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

interface SentryRouteProps extends ComponentPropsWithoutRef<typeof SentryRouteBase> {
  /**
   * Page title to display in browser. Can be overridden using react-helmet.
   */
  title?: string; // TODO: Make required
}

export const SentryRoute = ({
  title,
  component: Component,
  render,
  ...props
}: SentryRouteProps) => {
  if (!title) {
    return <SentryRouteBase {...props} />;
  }

  /**
   * Transform props.component to use props.render instead to inject <Helmet />
   */
  if (Component || render) {
    return (
      <SentryRouteBase
        {...props}
        render={(renderProps) => (
          <>
            <Helmet title={title} />
            {Component ? <Component {...renderProps} /> : render ? render(renderProps) : null}
          </>
        )}
      />
    );
  }

  return <SentryRouteBase {...props} />;
};

/**
 * Use to migrate individual routes to react-router v6
 */
export const SentryRouteV6 = ({
  title,
  component: Component,
  render,
  ...props
}: SentryRouteProps) => {
  if (!title) {
    return <SentryRouteBaseV6 {...props} />;
  }

  /**
   * Transform props.component to use props.render instead to inject <Helmet />
   */
  if (Component || render) {
    return (
      <SentryRouteBaseV6
        {...props}
        render={(renderProps: RouteComponentProps) => (
          <>
            <Helmet title={title} />
            {Component ? <Component {...renderProps} /> : render ? render(renderProps) : null}
          </>
        )}
      />
    );
  }

  return <SentryRouteBaseV6 {...props} />;
};
